import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import type { FinancialCharts as FinancialChartsData } from '@crm/shared';

interface ChartsProps {
  data: FinancialChartsData;
}

const PLAN_COLORS = ['#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444', '#6366F1'];

function formatCurrency(value: number): string {
  if (value >= 1000) {
    return `R$${(value / 1000).toFixed(1)}k`;
  }
  return `R$${value.toFixed(0)}`;
}

export function Charts({ data }: ChartsProps) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* MRR Evolution */}
      <div className="bg-white rounded-xl border border-secondary-200 p-5">
        <h3 className="text-sm font-semibold text-secondary-700 mb-4">Evolucao do MRR</h3>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={data.mrrEvolution}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="period" tick={{ fontSize: 11 }} />
            <YAxis tickFormatter={formatCurrency} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v) => [`R$${Number(v).toFixed(2)}`, 'MRR']} />
            <Line type="monotone" dataKey="mrr" stroke="#3B82F6" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Plan Distribution */}
      <div className="bg-white rounded-xl border border-secondary-200 p-5">
        <h3 className="text-sm font-semibold text-secondary-700 mb-4">Distribuicao por Plano</h3>
        <ResponsiveContainer width="100%" height={250}>
          <PieChart>
            <Pie
              data={data.planDistribution}
              dataKey="revenue"
              nameKey="plan"
              cx="50%"
              cy="50%"
              outerRadius={80}
              label={(entry: any) => `${entry.plan} (${entry.count})`}
            >
              {data.planDistribution.map((_, i) => (
                <Cell key={i} fill={PLAN_COLORS[i % PLAN_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip formatter={(v) => [`R$${Number(v).toFixed(2)}`, 'Receita']} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Churn Rate */}
      <div className="bg-white rounded-xl border border-secondary-200 p-5">
        <h3 className="text-sm font-semibold text-secondary-700 mb-4">Churn Rate</h3>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={data.churnRate}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="period" tick={{ fontSize: 11 }} />
            <YAxis tickFormatter={(v: number) => `${v.toFixed(1)}%`} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v) => [`${Number(v).toFixed(2)}%`, 'Churn']} />
            <Line
              type="monotone"
              dataKey="churnRate"
              stroke="#EF4444"
              strokeWidth={2}
              dot={{ r: 3 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Revenue Comparison */}
      <div className="bg-white rounded-xl border border-secondary-200 p-5">
        <h3 className="text-sm font-semibold text-secondary-700 mb-4">
          Receita Recebida vs Esperada
        </h3>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={data.revenueComparison}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="period" tick={{ fontSize: 11 }} />
            <YAxis tickFormatter={formatCurrency} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v) => [`R$${Number(v).toFixed(2)}`]} />
            <Legend />
            <Bar dataKey="received" name="Recebido" fill="#10B981" radius={[4, 4, 0, 0]} />
            <Bar dataKey="expected" name="Esperado" fill="#93C5FD" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
